/* =====================================================================
   ProMechBC — shopper-built product comparison.

   Scope of ownership: this file owns ONLY its own state (localStorage) and
   its own DOM (the dock, the overlay, the conflict dialog, and the
   [data-pmb-compare] toggles). It never mutates the cart and never touches
   Dawn's AJAX contracts — the comparison's per-product CTA is a plain link
   to the product page, so nothing here can desync cart.js / cart-drawer.js.

   Rules enforced here:
   - one comparison at a time, limited to a single product group (product
     type by default — scooters with scooters, parts with parts). Adding
     across groups asks before wiping, it never silently drops a selection.
   - at most `PMBCompare.max` products (theme setting, 2-4).
   - state survives navigation, and syncs across tabs via the storage event.

   Spec rows come from templates/product.pmb-compare.liquid, fetched lazily
   on open and memoised per page load.
   ===================================================================== */
(function () {
  'use strict';

  var CFG = window.PMBCompare;
  if (!CFG) return;

  var S = CFG.strings || {};
  var MAX = parseInt(CFG.max, 10) || 4;
  var MIN = parseInt(CFG.min, 10) || 2;
  var KEY = 'pmb:compare:v1';

  var state = { group: '', label: '', items: [], min: false };
  var detailCache = {};
  var pendingAdd = null;
  var lastFocus = null;
  var diffOnly = false;

  var dock, dockMain, slotsEl, liveEl, modal, sheetEl, bodyEl, summaryEl, ask, askBody;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- helpers ------------------------------------------------------ */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function say(msg) {
    if (typeof window.pmbToast === 'function') window.pmbToast(msg);
    if (liveEl) liveEl.textContent = msg;
  }
  function norm(v) { return String(v == null ? '' : v).replace(/\s+/g, ' ').trim().toLowerCase(); }

  /* ---- persistence -------------------------------------------------- */
  function load() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return;
      var o = JSON.parse(raw);
      if (o && Object.prototype.toString.call(o.items) === '[object Array]') {
        state = { group: o.group || '', label: o.label || '', items: o.items.slice(0, MAX), min: !!o.min };
      }
    } catch (e) { /* private mode / quota — compare just becomes per-page */ }
  }
  function save() {
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) {}
  }

  function indexOf(id) {
    for (var i = 0; i < state.items.length; i++) {
      if (String(state.items[i].id) === String(id)) return i;
    }
    return -1;
  }
  function has(id) { return indexOf(id) > -1; }

  /* ---- reading a toggle --------------------------------------------- */
  function readBtn(btn) {
    var d = btn.dataset;
    return {
      id: d.cmpId,
      handle: d.cmpHandle,
      title: d.cmpTitle,
      url: (d.cmpUrl || '').split('?')[0],
      img: d.cmpImg || '',
      price: d.cmpPrice || '',
      vendor: d.cmpVendor || '',
      group: d.cmpGroup || 'other',
      label: d.cmpGroupLabel || '',
      available: d.cmpAvailable === '1'
    };
  }

  /* ---- mutations ---------------------------------------------------- */
  function add(item, silent) {
    if (has(item.id)) return;
    if (state.items.length >= MAX) {
      say(String(S.full || 'Compare is full.').replace('[max]', MAX));
      bump();
      return;
    }
    if (!state.items.length) { state.group = item.group; state.label = item.label; }
    state.items.push(item);
    state.min = false;
    save();
    render();
    if (!silent) say(S.added || 'Added to compare');
  }

  function remove(id) {
    var i = indexOf(id);
    if (i < 0) return;
    state.items.splice(i, 1);
    if (!state.items.length) { state.group = ''; state.label = ''; }
    save();
    render();
    if (modal && !modal.hidden) {
      if (state.items.length < MIN) closeModal();
      else draw();
    }
  }

  function clearAll() {
    state = { group: '', label: '', items: [], min: false };
    save();
    render();
    closeModal();
  }

  /* ---- cross-group conflict ----------------------------------------- */
  function askConflict(item) {
    pendingAdd = item;
    var newLabel = item.label || (S.title || 'products');
    var body = String(S.conflictBody || '')
      .replace('[new]', newLabel)
      .replace('[n]', state.items.length)
      .replace('(s)', state.items.length === 1 ? '' : 's');
    if (askBody) askBody.textContent = body;
    if (!ask) return;
    lastFocus = document.activeElement;
    ask.hidden = false;
    requestAnimationFrame(function () {
      ask.classList.add('is-open');
      var ok = $('[data-cmp-ask-yes]', ask);
      if (ok) ok.focus();
    });
  }
  function closeAsk(accepted) {
    if (!ask || ask.hidden) return;
    ask.classList.remove('is-open');
    var item = pendingAdd;
    pendingAdd = null;
    window.setTimeout(function () { ask.hidden = true; }, reduced ? 0 : 200);
    if (accepted && item) {
      state = { group: item.group, label: item.label, items: [], min: false };
      add(item);
    } else if (lastFocus && lastFocus.focus) {
      lastFocus.focus();
    }
  }

  /* ---- dock rendering ------------------------------------------------ */
  /* The dock is fixed, so nothing in flow reserves space for it. Its height
     depends on the breakpoint, the collapsed state and the merchant's own
     button labels, so publish the measured value and let the stylesheet do the
     arithmetic rather than hard-coding a number that quietly goes stale. */
  function measureDock() {
    var root = document.documentElement;
    if (!dock || dock.hidden) { root.style.removeProperty('--cmp-dock-h'); return; }
    var h = dock.getBoundingClientRect().height;
    if (h > 0) root.style.setProperty('--cmp-dock-h', Math.ceil(h) + 'px');
  }

  /* The PDP's sticky add-to-cart bar (snippets/pmb-sticky-buy.liquid) is also
     fixed to the bottom and slides in on scroll. It owns the bottom edge, so
     the dock stacks on top of it rather than covering it: publish the bar's
     live height and let the stylesheet add it to the dock's offset and to the
     page's bottom clearance. We watch its class instead of touching its own
     IntersectionObserver, so the bar keeps sole ownership of when it shows. */
  var stickyBar = null;
  function measureStack() {
    var root = document.documentElement;
    var h = 0;
    if (stickyBar && stickyBar.classList.contains('is-visible')) {
      h = Math.ceil(stickyBar.getBoundingClientRect().height);
    }
    root.style.setProperty('--cmp-stack-h', h + 'px');
    root.classList.toggle('pmb-cmp-stacked', h > 0);
  }
  function watchStickyBar() {
    stickyBar = document.querySelector('[data-pmb-sticky-buy]');
    if (!stickyBar) return;
    measureStack();
    new MutationObserver(measureStack).observe(stickyBar, { attributes: true, attributeFilter: ['class'] });
    if (window.ResizeObserver) new ResizeObserver(measureStack).observe(stickyBar);
  }

  function bump() {
    if (!dock || reduced) return;
    dock.classList.remove('is-bumped');
    void dock.offsetWidth;
    dock.classList.add('is-bumped');
  }

  function render() {
    if (!dock) return;
    var n = state.items.length;

    dock.hidden = n === 0;
    dock.classList.toggle('is-min', !!state.min && n > 0);
    /* <html> flags drive the page's bottom clearance — the dock is fixed, so
       nothing else reserves space for it. */
    document.documentElement.classList.toggle('pmb-cmp-active', n > 0);
    document.documentElement.classList.toggle('pmb-cmp-min', !!state.min && n > 0);

    /* Readout hooks are namespaced `data-cmp-out` and queried only inside the
       dock/overlay. The toggles carry data-cmp-* attributes of their own, and
       a loose global selector here once matched them and overwrote each
       button's icons with the group name (same trap as `data-count` in
       pmb-site.js). Keep writes scoped and keep the two namespaces apart. */
    [dock, modal].forEach(function (scope) {
      if (!scope) return;
      $$('[data-cmp-out="count"]', scope).forEach(function (el) { el.textContent = n; });
      $$('[data-cmp-out="group"]', scope).forEach(function (el) {
        el.textContent = state.label || '';
        el.hidden = !state.label;
      });
    });

    if (slotsEl) {
      var html = '';
      state.items.forEach(function (it) {
        html +=
          '<li class="pmb-cmp-chip">' +
            '<span class="pmb-cmp-chip__img">' +
              (it.img ? '<img src="' + esc(it.img) + '" alt="" loading="lazy" width="72" height="72">' : '') +
            '</span>' +
            '<span class="pmb-cmp-chip__ttl">' + esc(it.title) + '</span>' +
            '<button type="button" class="pmb-cmp-chip__x" data-cmp-remove="' + esc(it.id) + '" ' +
              'aria-label="' + esc((S.remove || 'Remove') + ' — ' + it.title) + '">' +
              '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
            '</button>' +
          '</li>';
      });
      for (var i = n; i < MAX; i++) {
        html += '<li class="pmb-cmp-chip pmb-cmp-chip--ghost" aria-hidden="true"><span class="pmb-cmp-chip__plus">+</span><span class="pmb-cmp-chip__hint">' + esc(S.slot || 'Add a product') + '</span></li>';
      }
      slotsEl.innerHTML = html;
    }

    var go = $('[data-cmp-open]', dock);
    if (go) {
      go.disabled = n < MIN;
      go.setAttribute('aria-disabled', n < MIN ? 'true' : 'false');
      go.title = n < MIN ? (S.empty || '') : '';
    }

    var minBtn = $('[data-cmp-min]', dock);
    if (minBtn) minBtn.setAttribute('aria-expanded', state.min ? 'false' : 'true');

    syncToggles();
    measureDock();
  }

  function syncToggles(root) {
    var full = state.items.length >= MAX;
    $$('[data-pmb-compare]', root || document).forEach(function (b) {
      var on = has(b.dataset.cmpId);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      b.classList.toggle('is-on', on);
      b.classList.toggle('is-full', !on && full);
      var n = b.querySelector('[data-cmp-toggle-n]');
      if (n) n.textContent = state.items.length;
    });
  }

  /* ---- fetching the spec sheet --------------------------------------- */
  function fallbackDetail(it) {
    return {
      id: it.id, handle: it.handle, title: it.title, url: it.url,
      vendor: it.vendor, img: it.img, available: it.available,
      price: it.price, compareAt: '', priceRaw: null,
      rating: 0, ratingCount: 0, specs: [], partial: true
    };
  }

  function fetchDetail(it) {
    if (detailCache[it.handle]) return Promise.resolve(detailCache[it.handle]);
    var url = (it.url || '/products/' + it.handle) + '?view=pmb-compare';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (t) {
        var d = JSON.parse(t.trim());
        if (!d || !d.title) throw new Error('shape');
        if (!d.img) d.img = it.img;
        detailCache[it.handle] = d;
        return d;
      })
      .catch(function () { return fallbackDetail(it); });
  }

  /* ---- the comparison table ------------------------------------------ */
  function skeleton() {
    var cols = '';
    for (var i = 0; i < state.items.length; i++) {
      cols += '<div class="pmb-cmp-skel__col"></div>';
    }
    return '<div class="pmb-cmp-skel" role="status" aria-label="' + esc(S.loading || 'Loading') + '">' + cols + '</div>';
  }

  /* Union of spec labels, in the order each first appears. Keeping the
     first product's order means the column the shopper started from reads
     top-to-bottom the way its own spec sheet does. */
  function specRows(details) {
    var order = [];
    var seen = {};
    details.forEach(function (d) {
      (d.specs || []).forEach(function (s) {
        var k = norm(s.label);
        if (!k || seen[k]) return;
        seen[k] = true;
        order.push({ key: k, label: s.label });
      });
    });
    return order.map(function (row) {
      row.values = details.map(function (d) {
        var hit = (d.specs || []).filter(function (s) { return norm(s.label) === row.key; })[0];
        return hit ? hit.value : '';
      });
      return row;
    });
  }

  function rowIsSame(values) {
    var first = norm(values[0]);
    if (!first) return false;
    for (var i = 1; i < values.length; i++) {
      if (norm(values[i]) !== first) return false;
    }
    return true;
  }

  function stars(rating) {
    var pct = Math.max(0, Math.min(100, (parseFloat(rating) || 0) / 5 * 100));
    return '<span class="pmb-cmp-stars" style="--pct:' + pct.toFixed(1) + '%" aria-hidden="true"></span>';
  }

  function headCell(d, cheapest) {
    var isCheap = cheapest != null && d.priceRaw != null && Number(d.priceRaw) === cheapest && state.items.length > 1;
    return '' +
      '<th scope="col" class="pmb-cmp-hd">' +
        '<button type="button" class="pmb-cmp-hd__x" data-cmp-remove="' + esc(d.id) + '" aria-label="' + esc((S.remove || 'Remove') + ' — ' + d.title) + '">' +
          '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
        '</button>' +
        (isCheap ? '<span class="pmb-cmp-hd__flag">' + esc(S.bestPrice || 'Lowest price') + '</span>' : '') +
        '<a class="pmb-cmp-hd__img' + (d.img ? '' : ' is-empty') + '" href="' + esc(d.url) + '" tabindex="-1" aria-hidden="true">' +
          (d.img ? '<img src="' + esc(d.img) + '" alt="" loading="lazy" decoding="async">' : '') +
        '</a>' +
        (d.vendor ? '<span class="pmb-cmp-hd__brand">' + esc(d.vendor) + '</span>' : '') +
        '<a class="pmb-cmp-hd__ttl" href="' + esc(d.url) + '">' + esc(d.title) + '</a>' +
        (d.ratingCount ? '<span class="pmb-cmp-hd__rate">' + stars(d.rating) + '<span>' + esc(d.rating) + ' (' + esc(d.ratingCount) + ')</span></span>' : '') +
        '<span class="pmb-cmp-hd__price">' + esc(d.price) +
          (d.compareAt ? '<s>' + esc(d.compareAt) + '</s>' : '') +
        '</span>' +
        '<a class="btn btn--primary btn--sm pmb-cmp-hd__cta" href="' + esc(d.url) + '">' + esc(S.view || 'View product') +
          '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h13m-6-6 6 6-6 6"/></svg>' +
        '</a>' +
      '</th>';
  }

  function dataRow(label, values, opts) {
    opts = opts || {};
    var same = opts.forceDiff ? false : rowIsSame(values);
    var cells = values.map(function (v) {
      var empty = !norm(v);
      return '<td class="pmb-cmp-cell' + (empty ? ' is-na' : '') + '">' +
        (empty ? '<span aria-hidden="true">—</span><span class="visually-hidden">' + esc(S.na || 'Not published') + '</span>' : (opts.html ? v : esc(v))) +
        '</td>';
    }).join('');
    return '<tr class="pmb-cmp-row" data-same="' + (same ? '1' : '0') + '">' +
      '<th scope="row" class="pmb-cmp-k"><span class="pmb-cmp-k__t">' + esc(label) + '</span>' +
        (same ? '<span class="pmb-cmp-k__same" title="' + esc(S.same || '') + '" aria-hidden="true"></span>' : '') +
      '</th>' + cells + '</tr>';
  }

  /* Two scroll affordances the table needs to stay legible:
     - vertical: once the shopper scrolls past the product cards, collapse the
       sticky header to a compact name+price row so the specs keep the screen;
     - horizontal: fade the edge that still has columns behind it, so a phone
       shopper can see there is more than the two columns on screen. */
  function wireScroll(stage) {
    var sc = $('.pmb-cmp-scroll', stage);
    var tbl = $('.pmb-cmp-tbl', stage);
    if (!sc || !tbl) return;
    var head = $('thead', tbl);
    var expandedH = 0;
    var lastW = 0;
    var ticking = false;

    function paint() {
      ticking = false;

      /* The header is INSIDE the scroller it reacts to, so collapsing it
         shortens scrollHeight, the browser clamps scrollTop, that fires
         another scroll, and the header expands again — a slow limit cycle
         (measured: height oscillating 8px -> 37px while scrollTop drifted
         105 -> 48). Plain hysteresis does not fix it, because the clamp can
         jump straight past the lower threshold.

         The fix is NOT a big enter threshold. Sizing it off the header (0.7 of
         a ~400px header) meant three wheel notches before the fold moved, which
         reads as broken. What actually kills the cycle is a range guard: the
         clamp can only drag scrollTop back under the exit threshold if the
         range LEFT AFTER the fold is smaller than the enter threshold, so
         require `range > freed + enter` and the clamp has nowhere to run —
         scrollTop stays put and the state cannot flip.

         With that carrying the safety, the enter threshold is free to be a
         small absolute distance that the first notch clears. `freed` is a
         deliberate over-estimate (the fold really recovers ~0.3 of a header,
         mostly the photo stage), which only makes the guard stricter. */
      /* The yardstick has to be the SETTLED expanded height. The fold animates
         for ~0.6s and scroll events keep firing the whole way through it, so a
         plain last-measurement read latches a mid-animation height, shrinks
         both thresholds with it, and lets the header flip-flop. Keep the
         running maximum instead.
         Only a width change can honestly make the expanded header shorter
         (titles rewrap, columns narrow), and the scroller's width does not move
         when a mobile URL bar slides — so width, not the ResizeObserver, is
         what resets it. Unfold first, or the guard below has nothing to measure
         and the header would stay latched shut; this same pass re-folds it. */
      if (sc.clientWidth !== lastW) {
        lastW = sc.clientWidth;
        expandedH = 0;
        tbl.classList.remove('is-compact');
      }
      if (head && !tbl.classList.contains('is-compact')) {
        expandedH = Math.max(expandedH, head.getBoundingClientRect().height);
      }
      var range = sc.scrollHeight - sc.clientHeight;
      var isCompact = tbl.classList.contains('is-compact');
      if (expandedH > 0) {
        var enterY = Math.min(48, expandedH * 0.22);
        var exitY = enterY * 0.35;
        var freed = expandedH * 0.5;
        if (!isCompact && range > freed + enterY + 8 && sc.scrollTop > enterY) {
          tbl.classList.add('is-compact');
        } else if (isCompact && sc.scrollTop < exitY) {
          tbl.classList.remove('is-compact');
        }
      }

      var maxX = sc.scrollWidth - sc.clientWidth;
      stage.classList.toggle('has-x', maxX > 2);
      stage.classList.toggle('at-start', sc.scrollLeft <= 2);
      stage.classList.toggle('at-end', sc.scrollLeft >= maxX - 2);
    }
    sc.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(paint);
    }, { passive: true });
    if (window.ResizeObserver) new ResizeObserver(paint).observe(sc);
    paint();

    /* Column highlight. A table cannot express "the column under the pointer"
       in CSS, so publish the hovered index on the table and let one rule per
       possible column do the painting (max is 4). */
    tbl.addEventListener('mouseover', function (e) {
      var cell = e.target.closest && e.target.closest('td, th');
      if (!cell || !tbl.contains(cell)) return;
      var i = cell.cellIndex;
      if (i > 0 && !cell.classList.contains('pmb-cmp-grp__band')) tbl.setAttribute('data-col', i);
      else tbl.removeAttribute('data-col');
    });
    tbl.addEventListener('mouseleave', function () { tbl.removeAttribute('data-col'); });
  }

  function draw() {
    var details = state.items.map(function (it) {
      return detailCache[it.handle] || fallbackDetail(it);
    });
    if (!details.length) return;

    var prices = details.map(function (d) { return d.priceRaw == null ? null : Number(d.priceRaw); })
      .filter(function (v) { return v != null && !isNaN(v); });
    var cheapest = prices.length === details.length && prices.length > 1 ? Math.min.apply(null, prices) : null;
    if (cheapest != null && prices.every(function (v) { return v === cheapest; })) cheapest = null;

    var heads = details.map(function (d) { return headCell(d, cheapest); }).join('');

    /* Two honest groups. We do not invent a spec taxonomy the merchant never
       wrote — "at a glance" is what this file derives, "full specification" is
       whatever their spec metafields actually contain. */
    function groupRow(label) {
      return '<tr class="pmb-cmp-grp">' +
        '<th scope="row" class="pmb-cmp-k pmb-cmp-k--grp">' + esc(label) + '</th>' +
        '<td class="pmb-cmp-grp__band" colspan="' + details.length + '"></td>' +
      '</tr>';
    }

    var body = groupRow(S.groupGlance || 'At a glance');
    body += dataRow(S.price || 'Price', details.map(function (d) { return d.price; }));
    if (CFG.groupBy !== 'vendor') {
      body += dataRow(S.brand || 'Brand', details.map(function (d) { return d.vendor; }));
    }
    body += dataRow(S.availability || 'Availability', details.map(function (d) {
      return d.available ? (S.inStock || 'In stock') : (S.soldOut || 'Sold out');
    }));

    var rows = specRows(details);
    if (rows.length) {
      body += groupRow(S.groupSpecs || 'Full specification');
      rows.forEach(function (r) { body += dataRow(r.label, r.values); });
    }

    var diffCount = 0, rowTotal = 0;
    var tmp = document.createElement('tbody');
    tmp.innerHTML = body;
    $$('tr', tmp).forEach(function (tr) {
      var same = tr.getAttribute('data-same');
      if (same === null) return;                 /* group header, not a spec */
      rowTotal++;
      if (same === '0') diffCount++;
    });

    var partial = details.some(function (d) { return d.partial; });

    bodyEl.innerHTML =
      (partial ? '<p class="pmb-cmp-note">' + esc(S.failed || '') + '</p>' : '') +
      '<div class="pmb-cmp-stage at-start" data-cmp-stage>' +
        '<div class="pmb-cmp-scroll" tabindex="0" role="region" aria-label="' + esc(S.title || 'Compare') + '">' +
          '<table class="pmb-cmp-tbl' + (diffOnly ? ' is-diff' : '') + '" style="--cmp-cols:' + details.length + '">' +
            '<thead><tr><th scope="col" class="pmb-cmp-corner">' +
              '<span class="pmb-cmp-corner__n">' + details.length + '</span>' +
              '<span class="pmb-cmp-corner__t">' + esc(state.label || S.title || '') + '</span>' +
              '<span class="pmb-cmp-corner__d">' + diffCount + ' ' + esc(S.diffLabel || 'differences') + '</span>' +
              (diffCount < rowTotal
                ? '<span class="pmb-cmp-corner__key"><i aria-hidden="true"></i>' + esc(S.same || '') + '</span>'
                : '') +
            '</th>' + heads + '</tr></thead>' +
            '<tbody>' + body + '</tbody>' +
          '</table>' +
        '</div>' +
        '<span class="pmb-cmp-edge pmb-cmp-edge--l" aria-hidden="true"></span>' +
        '<span class="pmb-cmp-edge pmb-cmp-edge--r" aria-hidden="true"></span>' +
        '<span class="pmb-cmp-hint" aria-hidden="true">' + esc(S.scrollHint || '') + '</span>' +
      '</div>' +
      (diffCount === 0 ? '<p class="pmb-cmp-note pmb-cmp-note--flat">' + esc(S.noDiff || '') + '</p>' : '');

    if (summaryEl) {
      var tpl = diffCount ? (S.summary || '') : (S.summaryFlat || '');
      summaryEl.textContent = tpl.replace('[n]', details.length).replace('[d]', diffCount);
    }

    wireScroll($('[data-cmp-stage]', bodyEl));
  }

  /* ---- overlay open/close -------------------------------------------- */
  function openModal() {
    if (!modal) return;
    if (state.items.length < MIN) { say(S.empty || ''); bump(); return; }
    lastFocus = document.activeElement;
    modal.hidden = false;
    document.documentElement.classList.add('pmb-cmp-open');
    requestAnimationFrame(function () { modal.classList.add('is-open'); });
    bodyEl.innerHTML = skeleton();
    var closeBtn = $('[data-cmp-close].pmb-cmp-modal__x', modal);
    if (closeBtn) closeBtn.focus();

    var wanted = state.items.slice();
    Promise.all(wanted.map(fetchDetail)).then(function () {
      /* the shopper may have removed something while it loaded */
      if (modal.hidden) return;
      draw();
    });
  }

  function closeModal() {
    if (!modal || modal.hidden) return;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('pmb-cmp-open');
    window.setTimeout(function () {
      modal.hidden = true;
      bodyEl.innerHTML = '';
    }, reduced ? 0 : 260);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* Focus containment: the overlay covers the page, so Tab must not walk
     behind it. Cheaper and less brittle than inerting the whole document. */
  function onKey(e) {
    if (e.key === 'Escape') {
      if (ask && !ask.hidden) { closeAsk(false); return; }
      if (modal && !modal.hidden) { closeModal(); return; }
    }
    if (e.key !== 'Tab') return;
    var scope = (ask && !ask.hidden) ? ask : (modal && !modal.hidden) ? modal : null;
    if (!scope) return;
    var f = $$('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])', scope)
      .filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---- wiring --------------------------------------------------------- */
  function onClick(e) {
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;

    var toggle = t.closest('[data-pmb-compare]');
    if (toggle) {
      e.preventDefault();
      var item = readBtn(toggle);
      if (has(item.id)) { remove(item.id); say(S.removed || 'Removed'); return; }
      if (state.items.length && state.group !== item.group) { askConflict(item); return; }
      add(item);
      return;
    }

    var rm = t.closest('[data-cmp-remove]');
    if (rm) { e.preventDefault(); remove(rm.getAttribute('data-cmp-remove')); return; }

    if (t.closest('[data-cmp-open]')) { e.preventDefault(); openModal(); return; }
    if (t.closest('[data-cmp-clear]')) { e.preventDefault(); clearAll(); return; }
    if (t.closest('[data-cmp-close]')) { e.preventDefault(); closeModal(); return; }
    if (t.closest('[data-cmp-ask-yes]')) { e.preventDefault(); closeAsk(true); return; }
    if (t.closest('[data-cmp-ask-no]')) { e.preventDefault(); closeAsk(false); return; }

    if (t.closest('[data-cmp-min]')) {
      e.preventDefault();
      state.min = !state.min;
      save();
      render();
      return;
    }
  }

  function onChange(e) {
    var box = e.target.closest('[data-cmp-diff]');
    if (!box) return;
    diffOnly = box.checked;
    var tbl = $('.pmb-cmp-tbl', bodyEl);
    if (tbl) tbl.classList.toggle('is-diff', diffOnly);
  }

  function boot() {
    dock = $('#PmbCompareDock');
    modal = $('#PmbCompareModal');
    ask = $('#PmbCompareAsk');
    if (!dock || !modal) return;

    dockMain = $('#PmbCompareDockMain');
    slotsEl = $('[data-cmp-slots]', dock);
    liveEl = $('[data-cmp-live]', dock);
    sheetEl = $('.pmb-cmp-modal__sheet', modal);
    bodyEl = $('[data-cmp-body]', modal);
    summaryEl = $('[data-cmp-summary]', modal);
    askBody = $('[data-cmp-ask-body]', ask);

    load();
    render();

    if (window.ResizeObserver) {
      new ResizeObserver(measureDock).observe(dock);
    } else {
      window.addEventListener('resize', measureDock);
    }
    watchStickyBar();

    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);
    document.addEventListener('keydown', onKey);

    /* Another tab changed the selection — mirror it rather than fight it. */
    window.addEventListener('storage', function (e) {
      if (e.key !== KEY) return;
      load();
      render();
      if (modal && !modal.hidden) {
        if (state.items.length < MIN) closeModal(); else draw();
      }
    });

    /* Theme editor re-renders a section: re-mark whatever toggles it brought. */
    document.addEventListener('shopify:section:load', function (e) { syncToggles(e.target); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
